import React, { useEffect, useState } from "react";
import Header from "@/layouts/Header/Header";
import { Card, CardContent } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Quote, ArrowRight, Sparkles, Heart, Zap, Shield } from "lucide-react"
import { useNavigate } from "react-router-dom";
import HBN from "@/assets/HBN.svg";
import rakesh from "@/assets/rakesh-routhu.jpeg";
import ravi from "@/assets/ravi-velampally.jpeg";
import shamus from "@/assets/shamus-deveroux.jpeg";

const AboutUs = () => {
  const [isVisible, setIsVisible] = useState(false)
  const [activeSection, setActiveSection] = useState(0)
  const navigate = useNavigate();

  useEffect(() => {
    setIsVisible(true)
    const interval = setInterval(() => {
      setActiveSection((prev) => (prev + 1) % 3)
    }, 3000)
    return () => clearInterval(interval)
  }, [])

  const values = [
    { icon: Heart, title: "Built with Love", desc: "Every feature crafted with care" },
    { icon: Zap, title: "Lightning Fast", desc: "Quick connections, faster results" },
    { icon: Shield, title: "Trusted & Secure", desc: "Your data is always protected" },
  ]

  return (
    <>
      <Header />

      <main className="container mx-auto px-6 mt-16">
        <div className="container mx-auto px-6">
          {/* Hero Section with Animation */}
          <div
            className={`pt-16 pb-12 transform transition-all duration-1000 ${isVisible ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"}`}
          >
            <div className="text-center relative">
              <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-4">
                <Sparkles className="w-8 h-8 text-purple-400 animate-pulse" />
              </div>
              <h1 className="text-7xl md:text-8xl font-black bg-gradient-to-r from-purple-600 via-blue-600 to-purple-800 bg-clip-text text-transparent mb-4 tracking-tight">
                About Us
              </h1>
              <div className="w-24 h-1 bg-gradient-to-r from-purple-600 to-blue-600 mx-auto rounded-full mb-8"></div>
              <p className="text-xl text-gray-600 max-w-2xl mx-auto font-light leading-relaxed">
                Transforming the Australian home buying experience with{" "}
                <span className="font-semibold text-purple-600">innovation</span>,{" "}
                <span className="font-semibold text-blue-600">trust</span>, and{" "}
                <span className="font-semibold text-purple-600">technology</span>
              </p>
            </div>
          </div>

          {/* Our Story Section */}
          <div
            className={`grid lg:grid-cols-2 gap-16 items-center mb-20 transform transition-all duration-1000 delay-300 ${isVisible ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"}`}
          >
            <div className="space-y-6">
              <div className="inline-flex items-center bg-purple-100 text-purple-700 px-4 py-2 rounded-full text-sm font-semibold">
                <Sparkles className="w-4 h-4 mr-2" />
                Our Story
              </div>
              <h2 className="text-5xl font-bold text-gray-900 leading-tight">
                From <span className="text-purple-600">Vision</span> to <span className="text-blue-600">Reality</span>
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed">
                HBN was born from a simple yet powerful idea: home buying shouldn't be overwhelming. We've taken decades
                of trusted practices and supercharged them with modern technology.
              </p>
              <p className="text-lg text-gray-600 leading-relaxed">
                Every feature, every connection, every interaction is designed to make your journey smoother, faster, and
                more transparent than ever before.
              </p>
              {/* <Button className="group bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white px-8 py-4 rounded-full font-semibold shadow-lg hover:shadow-xl transition-all transform hover:scale-105">
                Discover Our Platform
                <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
              </Button> */}
            </div>

            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-purple-400 to-blue-400 rounded-3xl blur-3xl opacity-20 animate-pulse"></div>
              <Card className="relative bg-white/80 backdrop-blur-sm border-0 shadow-2xl rounded-3xl overflow-hidden">
                <CardContent className="p-8">
                  <div className="flex items-center justify-between mb-6">
                    <img
                      src={HBN} alt="HBN Platform" width={120} height={60} className="h-8 w-auto"
                    />
                    <div className="flex space-x-2">
                      <div className="w-3 h-3 bg-red-400 rounded-full"></div>
                      <div className="w-3 h-3 bg-yellow-400 rounded-full"></div>
                      <div className="w-3 h-3 bg-green-400 rounded-full"></div>
                    </div>
                  </div>
                  <div className="space-y-4">
                    <div className="h-4 bg-gradient-to-r from-purple-200 to-blue-200 rounded-full animate-pulse"></div>
                    <div className="h-4 bg-gradient-to-r from-blue-200 to-purple-200 rounded-full w-3/4 animate-pulse delay-100"></div>
                    <div className="h-4 bg-gradient-to-r from-purple-200 to-blue-200 rounded-full w-1/2 animate-pulse delay-200"></div>
                  </div>
                  <div className="mt-8 grid grid-cols-3 gap-4">
                    {[0, 1, 2].map((i) => (
                      <div
                        key={i}
                        className={`h-20 rounded-2xl transition-all duration-500 ${activeSection === i
                          ? "bg-gradient-to-br from-purple-400 to-blue-400 scale-105 shadow-lg"
                          : "bg-gradient-to-br from-gray-200 to-gray-300"
                          }`}
                      ></div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Values Section */}
          <div
            className={`mb-20 transform transition-all duration-1000 delay-500 ${isVisible ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"}`}
          >
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">What Drives Us Forward</h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">Three core principles that shape everything we do</p>
            </div>
            <div className="grid md:grid-cols-3 gap-8">
              {values.map((value, index) => (
                <Card
                  key={index}
                  className="group hover:shadow-2xl transition-all duration-500 hover:scale-105 hover:-translate-y-2 bg-white/80 backdrop-blur-sm border-0 rounded-2xl overflow-hidden"
                >
                  <CardContent className="p-8 text-center">
                    <div className="w-16 h-16 bg-gradient-to-r from-purple-500 to-blue-500 rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 group-hover:rotate-6 transition-all duration-300">
                      <value.icon className="w-8 h-8 text-white" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3">{value.title}</h3>
                    <p className="text-gray-600">{value.desc}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          {/* Mission Statement */}
          <div
            className={`text-center mb-20 transform transition-all duration-1000 delay-700 ${isVisible ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"}`}
          >
            <Card className="bg-gradient-to-r from-purple-600 to-blue-600 border-0 shadow-2xl rounded-3xl overflow-hidden">
              <CardContent className="p-12 relative">
                <div className="absolute top-0 left-0 w-full h-full bg-black/10"></div>
                <div className="relative z-10">
                  <h2 className="text-4xl font-bold text-white mb-6">Built by Home Buyers, for Home Buyers</h2>
                  <p className="text-xl text-white/90 max-w-4xl mx-auto leading-relaxed">
                    Our founders lived the challenges firsthand. We know the pain of complex processes and unreliable
                    professionals. That's why HBN gives you control, choice, and clarity in your home buying journey.
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Founder Section - Professional Design */}
          <div
            className={`mb-20 transform transition-all duration-1000 delay-900 ${isVisible ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"}`}
          >
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              <div className="relative">
                {/* Professional background with subtle pattern */}
                <div className="relative bg-gradient-to-br from-slate-100 to-white rounded-3xl p-8 shadow-2xl">
                  <div className="absolute inset-0 bg-gradient-to-br from-purple-50 to-blue-50 rounded-3xl opacity-50"></div>
                  <div className="relative aspect-[4/3] rounded-2xl overflow-hidden shadow-xl">
                    <img
                      src={ravi}
                      alt="Ravi Velampally, Founder & CEO"
                      fill
                      className="object-cover object-center"
                    />
                  </div>
                  {/* Decorative elements */}
                  <div className="absolute -top-4 -right-4 w-20 h-20 bg-purple-200 rounded-full opacity-30"></div>
                  <div className="absolute -bottom-4 -left-4 w-16 h-16 bg-blue-200 rounded-full opacity-40"></div>
                </div>
              </div>

              <div className="space-y-6">
                <Card className="bg-white border border-gray-100 shadow-xl rounded-2xl">
                  <CardContent className="p-8">
                    <div className="flex items-start space-x-4 mb-6">
                      <div className="w-16 h-16 bg-gradient-to-r from-purple-600 to-blue-600 rounded-full flex items-center justify-center flex-shrink-0">
                        <Quote className="w-8 h-8 text-white" />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-1">Founder's Vision</h3>
                        <p className="text-sm text-gray-500">Leading HBN's mission</p>
                      </div>
                    </div>
                    <p className="text-xl text-gray-700 leading-relaxed mb-6 font-light">
                      "With years in property investment and technology, I built HBN to give you real control and the
                      freedom to navigate your home buying journey exactly how you want."
                    </p>
                    <div className="flex items-center space-x-4 pt-4 border-t border-gray-100">
                      <div className="w-12 h-12 bg-gradient-to-r from-purple-600 to-blue-600 rounded-full flex items-center justify-center">
                        <span className="text-white font-bold text-lg">R</span>
                      </div>
                      <div>
                        <p className="font-bold text-gray-900 text-lg">Ravi Velampally</p>
                        <p className="text-purple-600 font-semibold">Founder & CEO</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>

          {/* Team Section - Elegant Professional Design */}
          <div
            className={`mb-20 transform transition-all duration-1000 delay-1100 ${isVisible ? "translate-y-0 opacity-100" : "translate-y-0 opacity-100"}`}
          >
            <div className="text-center mb-16">
              <div className="inline-flex items-center bg-gray-100 text-gray-700 px-6 py-3 rounded-full text-sm font-semibold mb-6">
                <Heart className="w-4 h-4 mr-2" />
                Leadership Team
              </div>
              <h2 className="text-5xl font-bold text-gray-900 mb-4">Meet Our Visionaries</h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                The experienced professionals driving HBN's mission to transform home buying
              </p>
            </div>

            <div className="max-w-6xl mx-auto">
              <div className="grid md:grid-cols-3 gap-12">

                {/* Rakesh Routhu */}
                <div className="group text-center order-2 md:order-1">
                  <div className="relative mb-8">
                    <div className="w-48 h-48 mx-auto relative">
                      <div className="absolute inset-0 bg-gradient-to-br from-blue-100 to-green-100 rounded-full"></div>
                      <div className="absolute inset-2 bg-white rounded-full shadow-lg">
                        <img
                          src={rakesh}
                          alt="Rakesh Routhu"
                          fill
                          className="rounded-full object-cover p-2"
                        />
                      </div>
                    </div>
                    <div className="absolute -bottom-2 left-1/2 transform -translate-x-1/2">
                      <div className="bg-green-600 text-white px-4 py-1 rounded-full text-sm font-semibold">
                        Strategy
                      </div>
                    </div>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">Rakesh Routhu</h3>
                  <p className="text-green-600 font-semibold mb-4">Strategic Advisor</p>
                  <p className="text-gray-600 leading-relaxed text-sm max-w-xs mx-auto">
                    Seasoned technology leader specializing in scaling digital platforms and delivering exceptional user
                    experiences.
                  </p>
                </div>

                {/* Ravi Velampally */}
                {/* Ravi Velampally */}
                <div className="group text-center order-1 md:order-2">
                  <div className="relative mb-8">
                    <div className="w-48 h-48 mx-auto relative">
                      <div className="absolute inset-0 bg-gradient-to-br from-purple-100 to-blue-100 rounded-full"></div>
                      <div className="absolute inset-2 bg-white rounded-full shadow-lg">
                        <img
                          src={ravi}
                          alt="Ravi Velampally"
                          fill
                          className="rounded-full object-cover p-2"
                        />
                      </div>
                    </div>
                    <div className="absolute -bottom-2 left-1/2 transform -translate-x-1/2">
                      <div className="bg-purple-600 text-white px-4 py-1 rounded-full text-sm font-semibold">
                        Founder
                      </div>
                    </div>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">Ravi Velampally</h3>
                  <p className="text-purple-600 font-semibold mb-4">Founder & CEO</p>
                  <p className="text-gray-600 leading-relaxed text-sm max-w-xs mx-auto">
                    Real estate investor and technology expert passionate about making home
                    ownership accessible through innovation.
                  </p>
                </div>


                {/* Shamus Deveroux */}
                <div className="group text-center order-3 md:order-3">
                  <div className="relative mb-8">
                    <div className="w-48 h-48 mx-auto relative">
                      <div className="absolute inset-0 bg-gradient-to-br from-green-100 to-yellow-100 rounded-full"></div>
                      <div className="absolute inset-2 bg-white rounded-full shadow-lg">
                        <img
                          src={shamus}
                          alt="Shamus Deveroux"
                          fill
                          className="rounded-full object-cover p-2"
                        />
                      </div>
                    </div>
                    <div className="absolute -bottom-2 left-1/2 transform -translate-x-1/2">
                      <div className="bg-green-600 text-white px-4 py-1 rounded-full text-sm font-semibold">Strategy</div>
                    </div>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">Shamus Deveroux</h3>
                  <p className="text-green-600 font-semibold mb-4">Strategic Advisor</p>
                  <p className="text-gray-600 leading-relaxed text-sm max-w-xs mx-auto">
                    Strategic advisor with extensive experience in finance and property markets, driving growth and market
                    insights.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* CTA Section */}
          <div
            className={`text-center mb-16 transform transition-all duration-1000 delay-1300 ${isVisible ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"}`}
          >
            <Card className="bg-gradient-to-r from-gray-900 to-gray-800 border-0 shadow-2xl rounded-3xl overflow-hidden">
              <CardContent className="p-16 relative">
                <div className="absolute inset-0 bg-gradient-to-r from-purple-600/20 to-blue-600/20"></div>
                <div className="relative z-10">
                  <h2 className="text-4xl font-bold text-white mb-6">Ready to Start Your Journey?</h2>
                  <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
                    Join thousands of Australians who trust HBN for their home buying needs
                  </p>
                  <Button className="group bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white px-10 py-4 text-lg font-semibold rounded-full shadow-lg hover:shadow-xl transition-all transform hover:scale-105"
                    onClick={() => navigate("/")}>
                    Get Started Today
                    <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
    </>
  );
};

export default AboutUs;
